import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, FormControl, FormControlLabel,
  FormHelperText, FormLabel, IconButton, InputAdornment, Radio, RadioGroup, TextField, Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, LocalShipping as DeliveryIcon } from '@mui/icons-material';
import { providerApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatPrice, getCurrencySymbol } from '../../utils/currencyUtils';
import {
  MAX_MONEY, numberProblem, previewDistanceFee, validateRings,
} from '../../utils/deliveryPricing';

const FLAT = 'FLAT';
const DISTANCE = 'DISTANCE';
const EMPTY_RING = { fromKm: '', toKm: '', surcharge: '' };

const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';
const str = (v) => (v === undefined || v === null ? '' : String(v));
const hasLocation = (p) =>
  p?.businessLat !== undefined && p?.businessLat !== null && p?.businessLat !== '' &&
  p?.businessLng !== undefined && p?.businessLng !== null && p?.businessLng !== '';

const formFromSettings = (settings, providerData) => ({
  pricingMode: settings?.pricingMode === DISTANCE ? DISTANCE : FLAT,
  baseFee: str(settings?.baseFee),
  freeThreshold: str(settings?.freeDeliveryThreshold),
  daysMin: str(settings?.deliveryDaysMin),
  daysMax: str(settings?.deliveryDaysMax),
  // 0 is how "no minimum" is stored (null would mean "leave unchanged"), so it shows as empty.
  minOrder: Number(providerData?.minimumOrderAmount) > 0 ? str(providerData.minimumOrderAmount) : '',
  rings: (settings?.distanceBands || []).map((b) => ({
    fromKm: str(b.fromKm), toKm: str(b.toKm), surcharge: str(b.surcharge),
  })),
});

/**
 * Client-side checks of the non-ring fields. `required` errors only show once the owner has tried
 * to save; the rest show as soon as a value is typed.
 */
const validateForm = (form, maxDays) => {
  const errors = {};
  if (isBlank(form.baseFee)) errors.baseFee = { key: 'delivery.errBaseFee', required: true };
  else if (numberProblem(form.baseFee) || Number(form.baseFee) > MAX_MONEY) errors.baseFee = { key: 'delivery.errBaseFee' };

  if (!isBlank(form.freeThreshold)
    && (numberProblem(form.freeThreshold) || Number(form.freeThreshold) <= 0 || Number(form.freeThreshold) > MAX_MONEY)) {
    errors.freeThreshold = { key: 'delivery.errThreshold' };
  }

  const min = Number(form.daysMin);
  const max = Number(form.daysMax);
  if (isBlank(form.daysMin)) errors.daysMin = { key: 'delivery.errDaysMin', required: true };
  else if (!Number.isInteger(min) || min < 1) errors.daysMin = { key: 'delivery.errDaysMin' };
  if (isBlank(form.daysMax)) errors.daysMax = { key: 'delivery.errDaysMax', required: true };
  else if (!Number.isInteger(max) || max < 1) errors.daysMax = { key: 'delivery.errDaysMax' };
  else if (!errors.daysMin && max < min) errors.daysMax = { key: 'delivery.errDaysOrder' };
  else if (maxDays && max > maxDays) errors.daysMax = { key: 'delivery.errDaysCap', params: { max: maxDays } };

  if (!isBlank(form.minOrder) && (numberProblem(form.minOrder) || Number(form.minOrder) > MAX_MONEY)) {
    errors.minOrder = { key: 'delivery.errMinOrder' };
  }
  return errors;
};

// ring validation error code -> i18n key
const RING_ERROR_KEYS = {
  none: 'delivery.ringErrNone',
  fromRequired: 'delivery.ringErrFromRequired',
  surchargeRequired: 'delivery.ringErrSurchargeRequired',
  negative: 'delivery.ringErrNegative',
  decimals: 'delivery.ringErrDecimals',
  invalid: 'delivery.ringErrInvalid',
  tooBig: 'delivery.ringErrTooBig',
  endAfterStart: 'delivery.ringErrEndAfterStart',
  firstNotZero: 'delivery.ringErrFirstNotZero',
  gap: 'delivery.ringErrGap',
  overlap: 'delivery.ringErrOverlap',
  openNotLast: 'delivery.ringErrOpenNotLast',
};

// The server's own message for a failed request, if it sent one.
const serverMessage = (err) => {
  const data = err?.response?.data;
  return (typeof data === 'string' ? data : data?.error || data?.message) || '';
};

// An empty minimum order is sent as 0: the server reads null as "leave unchanged", 0 as "no minimum".
const normalizeMinOrder = (v) => (isBlank(v) ? 0 : Number(v));

/**
 * The store's delivery settings: pricing mode (flat or by distance), base fee, free-delivery
 * threshold, promised delivery time and the minimum order amount. A store without saved settings
 * cannot take orders, which the banner says. The server re-validates everything and its message is
 * shown verbatim on a 400.
 */
const DeliverySettings = ({ providerData }) => {
  const { t } = useTranslation();
  const { refreshProviderData } = useAuth();
  const providerId = providerData?.id;

  const [status, setStatus] = useState('loading'); // loading | error | ready
  const [settings, setSettings] = useState(null); // the saved settings; null = never configured
  const [form, setForm] = useState(() => formFromSettings(null, providerData));
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [previewKm, setPreviewKm] = useState('8');
  const [previewBasket, setPreviewBasket] = useState('');
  // The store currency as the server resolves it (null = it cannot tell). The server owns it: the
  // dashboard never derives it from the products, and never sends a currency when saving.
  const [storeCurrency, setStoreCurrency] = useState(null);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const data = await providerApi.getDeliverySettings(providerId);
      let resolved = data ? data.storeCurrencyCode || null : null;
      // Unconfigured (204): ask the server which currency it will save. Not fatal: without it the
      // label is neutral and the server still resolves the currency on save.
      if (!data) {
        try {
          resolved = await providerApi.getStoreCurrency(providerId);
        } catch (err) {
          console.error('Failed to load the store currency:', err);
          resolved = null;
        }
      }
      setSettings(data);
      setStoreCurrency(resolved);
      setForm(formFromSettings(data, providerData));
      setStatus('ready');
    } catch (err) {
      console.error('Failed to load delivery settings:', err);
      setStatus('error');
    }
    // providerData only seeds the minimum-order field here; reloading on every profile refresh
    // would throw away what the owner is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  useEffect(() => {
    if (providerId) load();
  }, [providerId, load]);

  // The currency the next save will use: the server-resolved store currency. Unknown (several
  // product currencies, or none known) -> no symbol and a neutral label.
  const knownCurrency = storeCurrency;
  // The saved fees are in another currency than the store now sells in (its products' currency
  // changed). Saving again self-heals: the PUT carries no currency, so the server switches it.
  const currencyMismatch = Boolean(settings?.currencyCode && storeCurrency && settings.currencyCode !== storeCurrency);
  const currency = knownCurrency || t('delivery.currencyUnknown');
  const money = (amount) => (knownCurrency ? formatPrice(amount, knownCurrency) : Number(amount).toFixed(2));
  const symbol = knownCurrency ? getCurrencySymbol(knownCurrency) : '';
  const maxDays = settings?.maxDeliveryDays || null;
  const locationSet = hasLocation(providerData);
  const isDistance = form.pricingMode === DISTANCE;

  const fieldErrors = useMemo(() => validateForm(form, maxDays), [form, maxDays]);
  const ringErrors = useMemo(() => (isDistance ? validateRings(form.rings) : []), [isDistance, form.rings]);
  const showable = (e) => e && (showErrors || !e.required);
  const fieldError = (name) => {
    const e = fieldErrors[name];
    return showable(e) ? t(e.key, e.params) : '';
  };
  const ringError = (row, field) => {
    const e = ringErrors.find((x) => x.row === row && x.field === field && showable(x));
    return e ? t(RING_ERROR_KEYS[e.code], e.params) : '';
  };
  const tableError = ringErrors.find((x) => x.row === -1 && showable(x));

  const setField = (name) => (event) => {
    setForm((f) => ({ ...f, [name]: event.target.value }));
    setSaveSuccess('');
  };

  const setMode = (event) => {
    const mode = event.target.value;
    setForm((f) => ({
      ...f,
      pricingMode: mode,
      // First time on "By distance": start from a single open-ended ring covering everything.
      rings: mode === DISTANCE && f.rings.length === 0 ? [{ fromKm: '0', toKm: '', surcharge: '0' }] : f.rings,
    }));
    setSaveSuccess('');
  };

  const setRing = (index, field) => (event) => {
    const value = event.target.value;
    setForm((f) => ({ ...f, rings: f.rings.map((r, i) => (i === index ? { ...r, [field]: value } : r)) }));
    setSaveSuccess('');
  };

  const addRing = () => {
    setForm((f) => {
      const last = f.rings[f.rings.length - 1];
      return { ...f, rings: [...f.rings, { ...EMPTY_RING, fromKm: last ? last.toKm : '0' }] };
    });
  };

  const removeRing = (index) => {
    setForm((f) => ({ ...f, rings: f.rings.filter((_, i) => i !== index) }));
  };

  const preview = useMemo(() => {
    if (!isDistance) return null;
    return previewDistanceFee({
      baseFee: form.baseFee,
      rings: form.rings,
      distanceKm: previewKm,
      basketSubtotal: previewBasket,
      freeThreshold: form.freeThreshold,
    });
  }, [isDistance, form.baseFee, form.rings, form.freeThreshold, previewKm, previewBasket]);

  const handleSave = async () => {
    setSaveError('');
    setSaveSuccess('');
    setShowErrors(true);
    const blocked = Object.keys(fieldErrors).length > 0
      || (isDistance && (ringErrors.length > 0 || !locationSet));
    if (blocked) return;

    setSaving(true);
    const body = {
      pricingMode: form.pricingMode,
      baseFee: Number(form.baseFee),
      freeDeliveryThreshold: isBlank(form.freeThreshold) ? null : Number(form.freeThreshold),
      deliveryDaysMin: Number(form.daysMin),
      deliveryDaysMax: Number(form.daysMax),
    };
    // No currencyCode, ever: the server resolves the store's current currency on every save, so a
    // store whose product currency changed switches to it instead of being stuck on a 400.
    if (isDistance) {
      body.distanceBands = form.rings.map((r) => ({
        fromKm: Number(r.fromKm),
        toKm: isBlank(r.toKm) ? null : Number(r.toKm),
        surcharge: Number(r.surcharge),
      }));
    }

    let saved;
    try {
      saved = await providerApi.updateDeliverySettings(providerId, body);
    } catch (err) {
      setSaveError(serverMessage(err) || t('delivery.saveFailed'));
      setSaving(false);
      return;
    }
    setSettings(saved);
    // From now on show the currency the settings were just saved in (the store currency).
    setStoreCurrency(saved?.currencyCode || null);

    // The minimum order is a provider field, saved through PUT /providers/me. Send only what this
    // change needs: the endpoint treats null as "leave unchanged" for everything except the business
    // city/country ids, which it CLEARS when absent, so those are echoed from the latest server copy.
    // Never send `visible`: the server rejects visible=true for a provider without a priced service,
    // which is every normal store.
    let minOrderError = null;
    const nextMin = normalizeMinOrder(form.minOrder);
    if (nextMin !== normalizeMinOrder(providerData?.minimumOrderAmount)) {
      try {
        const latest = (await refreshProviderData()) || providerData;
        const payload = {
          minimumOrderAmount: nextMin,
          businessCityId: latest?.businessCityId ?? null,
          businessCountryId: latest?.businessCountryId ?? null,
        };
        // The minimum is on the items, i.e. in the store currency the server just resolved and
        // saved. Omitted when unknown, so the provider keeps whatever it had.
        if (saved?.currencyCode) payload.minimumOrderCurrencyCode = saved.currencyCode;
        await providerApi.updateProvider(providerId, payload);
        await refreshProviderData();
      } catch (err) {
        console.error('Failed to save minimum order amount:', err);
        minOrderError = { message: serverMessage(err) };
      }
    }

    setForm((f) => ({ ...formFromSettings(saved, providerData), minOrder: f.minOrder }));
    setShowErrors(false);
    if (minOrderError) {
      setSaveError(minOrderError.message
        ? `${t('delivery.minOrderSaveFailed')}: ${minOrderError.message}`
        : t('delivery.minOrderSaveFailed'));
    } else {
      setSaveSuccess(t('delivery.saved'));
    }
    setSaving(false);
  };

  if (status === 'loading') {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }
  if (status === 'error') {
    return (
      <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={load}>{t('common.retry')}</Button>}>
        {t('delivery.loadFailed')}
      </Alert>
    );
  }

  const moneyAdornment = <InputAdornment position="start">{symbol}</InputAdornment>;

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      {settings === null && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="delivery-setup-banner">
          {t('delivery.setupBanner')}
        </Alert>
      )}
      {currencyMismatch && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="delivery-currency-mismatch">
          {t('delivery.currencyMismatch', { saved: settings.currencyCode, store: storeCurrency })}
        </Alert>
      )}

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DeliveryIcon sx={{ mr: 1, color: 'primary.main' }} /> {t('delivery.settingsTitle')}
          </Typography>

          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>{saveSuccess}</Alert>}

          <FormControl sx={{ mb: 2 }}>
            <FormLabel id="delivery-pricing-mode-label">{t('delivery.pricingMode')}</FormLabel>
            <RadioGroup row aria-labelledby="delivery-pricing-mode-label" value={form.pricingMode} onChange={setMode}>
              <FormControlLabel value={FLAT} control={<Radio />} label={t('delivery.modeFlat')} />
              <FormControlLabel value={DISTANCE} control={<Radio />} label={t('delivery.modeDistance')} disabled={!locationSet} />
            </RadioGroup>
            {!locationSet && <FormHelperText>{t('delivery.noLocationHint')}</FormHelperText>}
          </FormControl>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <TextField
              label={t('delivery.baseFee')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ startAdornment: moneyAdornment }}
              value={form.baseFee}
              onChange={setField('baseFee')}
              error={Boolean(fieldError('baseFee'))}
              helperText={fieldError('baseFee') || t(isDistance ? 'delivery.baseFeeDistanceHelper' : 'delivery.baseFeeFlatHelper', { currency })}
              fullWidth
            />
            <TextField
              label={t('delivery.freeThreshold')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ startAdornment: moneyAdornment }}
              value={form.freeThreshold}
              onChange={setField('freeThreshold')}
              error={Boolean(fieldError('freeThreshold'))}
              helperText={fieldError('freeThreshold') || t('delivery.freeThresholdHelper')}
              fullWidth
            />
            <TextField
              label={t('delivery.daysMin')}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={form.daysMin}
              onChange={setField('daysMin')}
              error={Boolean(fieldError('daysMin'))}
              helperText={fieldError('daysMin') || t('delivery.daysHelper')}
              fullWidth
            />
            <TextField
              label={t('delivery.daysMax')}
              type="number"
              inputProps={{ min: 1, max: maxDays || undefined, step: 1 }}
              value={form.daysMax}
              onChange={setField('daysMax')}
              error={Boolean(fieldError('daysMax'))}
              helperText={fieldError('daysMax') || (maxDays ? t('delivery.daysMaxHelper', { max: maxDays }) : t('delivery.daysHelper'))}
              fullWidth
            />
            <TextField
              label={t('delivery.minOrder')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ startAdornment: moneyAdornment }}
              value={form.minOrder}
              onChange={setField('minOrder')}
              error={Boolean(fieldError('minOrder'))}
              helperText={fieldError('minOrder') || t('delivery.minOrderHelper', { currency })}
              fullWidth
            />
          </Box>

          {isDistance && (
            <Box sx={{ mt: 3 }} data-testid="delivery-rings">
              <Typography variant="subtitle1" fontWeight="bold">{t('delivery.ringsTitle')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('delivery.ringsHelp')}</Typography>
              {!locationSet && <Alert severity="warning" sx={{ mb: 2 }}>{t('delivery.errNoLocation')}</Alert>}

              {form.rings.map((ring, index) => (
                <Box
                  key={index}
                  role="group"
                  aria-label={t('delivery.ringLabel', { n: index + 1 })}
                  sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr 1fr auto', alignItems: 'start', mb: 1.5 }}
                >
                  <TextField
                    label={t('delivery.ringFrom')}
                    type="number"
                    size="small"
                    inputProps={{ min: 0, step: '0.01' }}
                    value={ring.fromKm}
                    onChange={setRing(index, 'fromKm')}
                    error={Boolean(ringError(index, 'fromKm'))}
                    helperText={ringError(index, 'fromKm')}
                  />
                  <TextField
                    label={t('delivery.ringTo')}
                    type="number"
                    size="small"
                    inputProps={{ min: 0, step: '0.01' }}
                    placeholder={t('delivery.ringToPlaceholder')}
                    InputLabelProps={{ shrink: true }}
                    value={ring.toKm}
                    onChange={setRing(index, 'toKm')}
                    error={Boolean(ringError(index, 'toKm'))}
                    helperText={ringError(index, 'toKm')}
                  />
                  <TextField
                    label={t('delivery.ringSurcharge')}
                    type="number"
                    size="small"
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{ startAdornment: moneyAdornment }}
                    value={ring.surcharge}
                    onChange={setRing(index, 'surcharge')}
                    error={Boolean(ringError(index, 'surcharge'))}
                    helperText={ringError(index, 'surcharge')}
                  />
                  <Tooltip title={t('delivery.removeRing', { n: index + 1 })}>
                    <IconButton
                      aria-label={t('delivery.removeRing', { n: index + 1 })}
                      onClick={() => removeRing(index)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}

              {tableError && <Alert severity="error" sx={{ mb: 1.5 }}>{t(RING_ERROR_KEYS[tableError.code])}</Alert>}

              <Button startIcon={<AddIcon />} onClick={addRing} size="small">{t('delivery.addRing')}</Button>

              <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: 'action.hover' }} data-testid="delivery-preview">
                <Typography variant="subtitle2" gutterBottom>{t('delivery.previewTitle')}</Typography>
                <TextField
                  label={t('delivery.previewDistance')}
                  type="number"
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                  value={previewKm}
                  onChange={(e) => setPreviewKm(e.target.value)}
                  sx={{ mb: 1.5, maxWidth: 220 }}
                />
                <TextField
                  label={t('delivery.previewBasket')}
                  type="number"
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ startAdornment: moneyAdornment }}
                  value={previewBasket}
                  onChange={(e) => setPreviewBasket(e.target.value)}
                  sx={{ mb: 1.5, ml: { sm: 1.5 }, maxWidth: 220 }}
                />
                <Typography variant="body2" role="status">
                  {preview?.status === 'ok' && !preview.waived && t('delivery.previewResult', {
                    km: Number(previewKm),
                    base: money(preview.base),
                    surcharge: money(preview.surcharge),
                    total: money(preview.total),
                  })}
                  {preview?.status === 'ok' && !preview.waived && preview.freeThreshold !== null && isBlank(previewBasket) && (
                    <Box component="span" sx={{ ml: 1 }}>
                      {t('delivery.previewFreeFrom', { threshold: money(preview.freeThreshold) })}
                    </Box>
                  )}
                  {preview?.status === 'ok' && preview.waived && t('delivery.previewWaived', {
                    km: Number(previewKm),
                    basket: money(Number(previewBasket)),
                    fee: money(preview.base + preview.surcharge),
                  })}
                  {preview?.status === 'outOfRange' && t('delivery.previewOutOfRange', { km: Number(previewKm) })}
                  {preview?.status === 'invalid' && t('delivery.previewInvalid')}
                  {preview?.status === 'badDistance' && t('delivery.previewEnterDistance')}
                  {preview?.status === 'badBaseFee' && t('delivery.previewBadBaseFee')}
                  {preview?.status === 'badBasket' && t('delivery.previewBadBasket')}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleSave} disabled={saving || (isDistance && !locationSet)} sx={{ borderRadius: 2 }}>
              {saving ? <CircularProgress size={22} color="inherit" /> : t('delivery.save')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DeliverySettings;
